import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { showErrorToast } from '@Helpers';

import { sendErrorEvent } from '@Analytics';
import {} from '@Components';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { useFetchData } from '@CustomHooks';
import { Options } from '@Models';
import { bopsApi } from '@Network';
import { AppDispatch, tableServerRevision } from '@Store';
import Modal from '../ui/modal';
import ModalLoading from '../ui/modal-loading';
import ModalSuccess from '../ui/modal-success';
import MultiSelect from '../ui/multiselect';
import Spinner from '../ui/spinner';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchUsers: () => void;
  oldTags?: string[];
  IncrementEvent: (event: string) => void;
  clientValue: Options | undefined;
  email: string;
}

const UserEditTagsModal = ({
  isOpen,
  onClose,
  fetchUsers,
  oldTags = [],
  clientValue,
  IncrementEvent,
  email,
}: UserModalProps) => {
  const { t } = useTranslation();

  const [currentTags, setCurrentTags] = useState<string[]>(oldTags);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openLoading, setOpenLoading] = useState(false);

  const {
    data: clientTags,
    error: errorClientTags,
    isLoading: clientTagsLoading,
    refetch: refetchClientTags,
  } = useFetchData(bopsApi.getClientTags, null, true);

  React.useEffect(() => {
    if (!clientValue?.value) return;
    refetchClientTags(clientValue?.value);
    setCurrentTags(oldTags);
  }, [isOpen]);

  const onSubmit = () => {
    setOpenLoading(true);

    bopsApi
      .updateUserTags(email, clientValue?.value ?? '', currentTags)
      .then(() => {
        onClose();
        setOpenLoading(false);
        setOpenSuccess(true);
        setTimeout(() => {
          IncrementEvent('usersEvent');
          fetchUsers();
        }, 3000);
      })
      .catch((error) => {
        sendErrorEvent(error);
        setOpenLoading(false);
        const errorMessage = t('User.EditTagsError');
        showErrorToast(errorMessage);
      });
  };

  return (
    <>
      <Modal modalOpen={isOpen} closeModal={onClose} title={t('User.EditTags')} closeClickOutside={false}>
        <div className="w-full">
          <div className="text-sm">
            {clientTags ? (
              clientTags.length > 0 ? (
                <MultiSelect
                  placeholder={t('User.EditTagsPlaceholder')}
                  emptyText={t('User.EditTagsEmpty')}
                  options={
                    clientTags
                      ? clientTags
                          .filter((tag) => !currentTags.includes(tag))
                          .map((tag) => ({ label: tag, value: tag }))
                      : []
                  }
                  selected={currentTags.map((tag) => ({ label: tag, value: tag }))}
                  onChange={(selected) => {
                    setCurrentTags(selected.map((option) => option.value));
                  }}
                />
              ) : (
                <p className="text-sm text-red-500">{`${t('User.EditTagsEmpty')}`} </p>
              )
            ) : clientTagsLoading ? (
              <Spinner />
            ) : (
              errorClientTags && <p className="text-sm text-red-500">{`${t('User.EditTagsLoadingError')}`} </p>
            )}
          </div>
          <div className="flex gap-2 justify-center mt-4 w-full">
            <TrackedButton
              variant="outline"
              buttonName="EDIT_USER_CANCELED"
              onClick={() => {
                onClose();
              }}
              className="w-full bg-white"
              isSubmit={false}>
              {t('Global.Canceled')}
            </TrackedButton>
            <TrackedButton onClick={onSubmit} isSubmit={false} buttonName="USER_EDITED" className="w-full">
              <div className="text-white"> {t('User.Edit')} </div>
            </TrackedButton>
          </div>
        </div>
      </Modal>

      <ModalSuccess isOpen={openSuccess} onClose={() => setOpenSuccess(false)}>
        <div className="text-center">
          <span className="text-[18px]"> {t('User.EditSuccess')} </span>
        </div>
      </ModalSuccess>

      <ModalLoading isOpen={openLoading} onClose={() => setOpenLoading(false)}>
        <div className="text-center -mt-[9px]">{t('User.LoadingEdit')}</div>
      </ModalLoading>
    </>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  const { IncrementEvent } = tableServerRevision;

  return {
    IncrementEvent: (event: string) => dispatch(IncrementEvent(event)),
  };
};

export default connect(null, mapDispatchToProps)(UserEditTagsModal);
